/**
 * v2 onboarding draft utilities.
 * Handles localStorage read/write and v1-compatible migration.
 * Never writes partial data to Supabase — only localStorage.
 */
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';
import { defaultFormData } from '@/components/onboarding/onboardingDefaults';

const DRAFT_KEY = 'businessSignupData';

/**
 * A stored draft: the canonical form data plus persistence metadata.
 * Metadata keys are underscore-prefixed and are never sent to Supabase.
 */
export type V2Draft = BusinessSignupData & {
  /**
   * Booking flow / link-in-bio theme key (see BOOKING_THEMES), persisted to
   * business_profiles.booking_flow_theme_key on launch. Lives here rather than
   * on BusinessSignupData because v1 is frozen and never sets it.
   */
  bookingFlowThemeKey?: string;
  /** ISO timestamp of the last write — drives the resume banner. */
  _savedAt?: string;
  /** True when the draft was seeded by the pre-signup preview builder. */
  _previewOrigin?: boolean;
};

/** Theme applied when a draft has never chosen one. */
export const DEFAULT_BOOKING_THEME_KEY = 'figgy_purple';

/**
 * Load a draft from localStorage.
 * Attempts to use v1 drafts directly (same BusinessSignupData type).
 * Returns null if no draft, parse fails, or draft is a completed v1 session.
 */
export function loadV2Draft(): V2Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<V2Draft>;

    // Completed v1 session — clear and restart
    if (Array.isArray(parsed.completedSteps) && parsed.completedSteps.includes(8)) {
      console.log('[v2 onboarding] Draft was a completed v1 session — clearing.');
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }

    // Merge into defaultFormData to fill any missing v2-era fields
    const merged: V2Draft = {
      ...defaultFormData,
      ...parsed,
      // Ensure nested objects are also merged with defaults, not replaced
      workingHours: {
        ...defaultFormData.workingHours,
        ...(parsed.workingHours || {}),
      },
      services: Array.isArray(parsed.services) ? parsed.services : [],
      completedSteps: [], // Do not trust v1 completedSteps for v2 step routing
      // Carried through explicitly so these survive the defaults merge
      bookingFlowThemeKey: parsed.bookingFlowThemeKey || DEFAULT_BOOKING_THEME_KEY,
      _savedAt: parsed._savedAt,
      _previewOrigin: parsed._previewOrigin === true,
    };

    // Sanitize startDate
    if (!merged.startDate || isNaN(new Date(merged.startDate).getTime())) {
      merged.startDate = new Date().toISOString().split('T')[0];
    }

    // logoFile cannot survive serialization — always null after reload
    merged.logoFile = null;

    return merged;
  } catch (err) {
    console.warn('[v2 onboarding] Draft parse failed — clearing. Reason:', err);
    localStorage.removeItem(DRAFT_KEY);
    return null;
  }
}

/**
 * Persist the current draft to localStorage.
 * Throws if storage is unavailable or full — callers that can surface the
 * failure to the user should catch; background autosaves may ignore it.
 */
export function saveV2Draft(data: V2Draft): void {
  // File objects cannot be serialized — strip before saving
  const serializable = { ...data, logoFile: null };
  localStorage.setItem(DRAFT_KEY, JSON.stringify(serializable));
}

/** Clear the draft after a successful launch. */
export function clearV2Draft(): void {
  localStorage.removeItem(DRAFT_KEY);
}

/**
 * V2 step numbers (1-indexed, matching ?step= URL params).
 */
export const V2_STEPS = {
  ACCOUNT:   1,
  BUSINESS:  2,
  SERVICES:  3,
  SCHEDULE:  4,
  BRANDING:  5,
  DONE:      6,
} as const;

export type V2StepNumber = typeof V2_STEPS[keyof typeof V2_STEPS];

/**
 * Derive the earliest incomplete v2 step from actual data.
 * Does NOT trust v1 completedSteps — uses field presence instead.
 */
export function deriveV2ResumeStep(data: BusinessSignupData): V2StepNumber {
  // Step 1: Account — need to be signed in
  if (!data.ssoConnected && !data.email) return V2_STEPS.ACCOUNT;

  // Step 2: Business basics
  if (
    !data.fullName?.trim() ||
    !data.businessName?.trim() ||
    !data.businessAddress?.trim() ||
    !data.timezone ||
    !data.bookingUrl?.trim()
  ) return V2_STEPS.BUSINESS;

  // Step 3: Services — at least one required
  if (!Array.isArray(data.services) || data.services.length === 0) return V2_STEPS.SERVICES;

  // Step 4: Schedule — at least one enabled day
  const hasEnabledDay = data.workingHours
    ? Object.values(data.workingHours).some(d => d?.enabled)
    : false;
  if (!hasEnabledDay) return V2_STEPS.SCHEDULE;

  // Step 5: Branding — title is required
  if (!data.title?.trim()) return V2_STEPS.BRANDING;

  // All fields complete — resume at Branding so user can launch from there
  return V2_STEPS.BRANDING;
}
